/**
 * lib/ui.js -- Port PARCIAL de src/js/ui.js (app/frontend). Cubre lo que
 * Fase 1 necesitaba (loader global + toasts + escapeHtml) y lo que Fase 2
 * (Pedidos) agrega: showConfirm/showAlert, que records-mutations.js usa
 * para aprobar/rechazar/confirmar entrega simple y para avisos
 * bloqueantes (RECORDS_DEAD_WINDOW, etc). El resto (showNotice,
 * showConfirmationRequired, withScopedLoader, tooltips, helpers de
 * combos de catalogo) se porta cuando su fase llegue (YAGNI).
 *
 * OJO -- esto NO son componentes React, son las MISMAS funciones DOM
 * imperativas del original, con entrada/salida visual compartida. Funcionan
 * igual de bien aqui porque `#global-loader` y `#toast-root` viven como
 * markup ESTATICO en index.html (fuera de <div id="root">, ver ese
 * archivo) -- React nunca los toca. showConfirm/showAlert crean su propio
 * overlay dinamicamente (igual que el original), tambien fuera del
 * arbol de React.
 *
 * showAccountDisabled() es la unica excepcion que SI importa algo:
 * necesita clearSession() para cerrar la sesion del lado del cliente
 * cuando deshabilitan la cuenta a mitad de sesion (doc 22 §8). No hay
 * ciclo: sessionStore.js no importa nada de aqui.
 */
import { closeDialogMotion } from './dialogMotion.js';
import '../css/dialog-motion.css';
import '../css/feedback.css';
import { activateDialog } from './dialogFocus.js';
import { enableSheetDrag } from './sheet.js';
import { clearSession } from './sessionStore.js';

export function showGlobalLoader(text = 'Procesando...') {
  const loader = document.getElementById('global-loader');
  const label = document.getElementById('global-loader-text');
  if (label) label.textContent = text;
  loader?.classList.remove('hidden');
}

export function hideGlobalLoader() {
  document.getElementById('global-loader')?.classList.add('hidden');
}

export async function withLoader(task, message = 'Cargando información...') {
  showGlobalLoader(message);
  try { return await task(); } finally { hideGlobalLoader(); }
}

export { showToast } from './toasts.js';

export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Guard all imperative dialog exits, including repeated Escape/confirm events.
const closingDialogs = new WeakMap();
function resetDialogExit(overlay) {
  closingDialogs.get(overlay)?.();
  closingDialogs.delete(overlay);
}
function dismissDialog(overlay, finish) {
  if (closingDialogs.has(overlay)) return;
  closingDialogs.set(overlay, () => {});
  const dispose = closeDialogMotion(overlay, () => {
    resetDialogExit(overlay);
    finish();
  });
  if (closingDialogs.has(overlay)) closingDialogs.set(overlay, dispose);
}

// Arrastre de la hoja inferior (movil): showConfirm/showAlert reutilizan el MISMO overlay,
// asi que solo puede haber un arrastre vivo por elemento (si no, los listeners se acumulan).
// Solo los dialogos que se pueden cerrar lo usan; los bloqueantes no llevan asa.
const sheetDrags = new WeakMap();
function bindSheetDrag(overlay, close) {
  sheetDrags.get(overlay)?.();
  sheetDrags.set(overlay, enableSheetDrag(overlay, () => close));
}
function unbindSheetDrag(overlay) {
  sheetDrags.get(overlay)?.();
  sheetDrags.delete(overlay);
}
const SHEET_HANDLE = '<div class="sheet-handle" aria-hidden="true"></div>';

export function showConfirm({ title = 'Confirmar', message = '', confirmLabel = 'Aceptar', cancelLabel = 'Cancelar', extraLabel = '', wide = false, danger = false, onConfirm, onCancel, onExtra }) {
  let overlay = document.getElementById('confirm-modal');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'confirm-modal';
    overlay.className = 'modal-overlay hidden';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    document.body.appendChild(overlay);
  }
  resetDialogExit(overlay);
  overlay.innerHTML = [
    `<div class="modal-box${wide ? ' confirm-modal-wide' : ''}" style="--modal-max-width:${wide ? 'min(43rem, calc(100vw - 2rem))' : '460px'};">`,
    SHEET_HANDLE,
    '<div class="modal-scroll" role="region" aria-label="Contenido del diálogo" tabindex="0">',
    '<div class="modal-header"><h3 class="modal-title">' + escapeHtml(title) + '</h3></div>',
    '<div class="modal-body"><p style="margin:0;">' + message + '</p></div>',
    '</div>',
    '<div class="modal-footer' + (wide ? ' confirm-modal-actions' : '') + '">',
    extraLabel ? ('<button class="btn btn-danger btn-danger-tonal" id="confirm-extra-btn">' + escapeHtml(extraLabel) + '</button>') : '',
    '<button class="btn btn-secondary" id="confirm-cancel-btn">' + escapeHtml(cancelLabel) + '</button>',
    // La confirmación destructiva usa rojo; Cancelar conserva la acción secundaria.
    '<button class="' + (danger ? 'btn btn-danger-primary' : 'btn btn-primary') + '" id="confirm-ok-btn">' + escapeHtml(confirmLabel) + '</button>',
    '</div></div>',
  ].join('');
  overlay.classList.remove('hidden');
  let releaseFocus;
  const close = action => dismissDialog(overlay, () => { overlay.classList.add('hidden'); unbindSheetDrag(overlay); releaseFocus?.(); action?.(); });
  releaseFocus = activateDialog(overlay, { onClose: () => close(onCancel), initialFocus: document.getElementById('confirm-cancel-btn') });
  bindSheetDrag(overlay, () => close(onCancel));
  document.getElementById('confirm-cancel-btn').onclick = () => close(onCancel);
  document.getElementById('confirm-ok-btn').onclick = () => close(onConfirm);
  if (extraLabel) document.getElementById('confirm-extra-btn').onclick = () => close(onExtra);
  overlay.onclick = event => { if (event.target === overlay) close(onCancel); };
}

/** Puente a doc 19 CONFIRMATION_REQUIRED, usado por catalogos/gerentes/
 * subgerentes/proveedores cuando el nombre ya existe pero esta INACTIVO:
 * envoltorio delgado sobre showConfirm con el texto/orden fijo. */
export function showConfirmationRequired(confirmation, { onReactivate, onCancel } = {}) {
  showConfirm({
    title: 'Ya existe, pero está inactivo',
    message: escapeHtml(confirmation?.message || '¿Deseas reactivar el registro existente?'),
    confirmLabel: 'Reactivar',
    cancelLabel: 'Cancelar',
    onConfirm: () => onReactivate?.(confirmation?.existingId),
    onCancel,
  });
}

const ALERT_VARIANTS = {
  success: { icon: 'check_circle', color: 'var(--color-success)' },
  error: { icon: 'error', color: 'var(--color-danger)' },
  warning: { icon: 'warning', color: 'var(--color-warning-text)' },
  info: { icon: 'info', color: 'var(--color-primary)' },
};

export function showAlert({ title = '', message = '', type = 'info', okLabel = 'Entendido', onClose }) {
  let overlay = document.getElementById('alert-modal');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'alert-modal';
    overlay.className = 'modal-overlay hidden';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    document.body.appendChild(overlay);
  }
  const variant = ALERT_VARIANTS[type] || ALERT_VARIANTS.info;
  resetDialogExit(overlay);
  overlay.innerHTML = [
    '<div class="modal-box" style="--modal-max-width:460px;">',
    SHEET_HANDLE,
    '<div class="modal-scroll" role="region" aria-label="Contenido del diálogo" tabindex="0">',
    '<div class="modal-header" style="justify-content:flex-start; gap:0.6rem;">',
    '<span class="material-symbols-rounded" aria-hidden="true" style="color:' + variant.color + '; font-size:1.4rem;">' + variant.icon + '</span>',
    '<h3 class="modal-title">' + escapeHtml(title) + '</h3>',
    '</div>',
    '<div class="modal-body"><p style="margin:0; white-space:pre-line;">' + escapeHtml(message) + '</p></div>',
    '</div>',
    '<div class="modal-footer modal-footer-center">',
    '<button class="btn btn-secondary" id="alert-ok-btn">' + escapeHtml(okLabel) + '</button>',
    '</div></div>',
  ].join('');
  overlay.classList.remove('hidden');
  let releaseFocus;
  const close = () => dismissDialog(overlay, () => { overlay.classList.add('hidden'); unbindSheetDrag(overlay); releaseFocus?.(); onClose?.(); });
  releaseFocus = activateDialog(overlay, { onClose: close });
  bindSheetDrag(overlay, close);
  document.getElementById('alert-ok-btn').onclick = close;
  document.getElementById('alert-ok-btn').focus();
}

/** "Ya no tienes acceso a este modulo" -- doc 22 §8 (validacion en vivo).
 * Distinto de showAlert(): cuando se revoca un MODULO completo (no una
 * accion puntual), cerrar el aviso "de lado" deja al usuario viendo una
 * pantalla que ya no puede cargar nada (quedo varado). Por eso este modal
 * NO tiene cierre por clic-afuera ni Escape (igual que showAlert/
 * showConfirm, que tampoco lo tienen hoy) -- el UNICO camino es el boton.
 *
 * El boton hace reload() (NO `href = '/'`): esta funcion la comparten el
 * portal interno Y el portal proveedor (ambos pasan por el mismo
 * lib/api.js), y cada uno es un bundle/origen DISTINTO (ver
 * entries/proveedor.jsx) -- un `href = '/'` absoluto manda al portal
 * interno si el proveedor corre bajo `/proveedor.html` en vez de su
 * propio subdominio. `reload()` siempre se queda en el origen correcto, y
 * como la sesion vive solo en memoria (sessionStore.js), recargar ya
 * fuerza un resume() fresco con los permisos al dia -- sin esto, no hacia
 * falta nada mas. */
export function showModuleAccessDenied({ moduleTitle = 'este módulo' } = {}) {
  let overlay = document.getElementById('module-denied-modal');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'module-denied-modal';
    overlay.className = 'modal-overlay hidden';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    document.body.appendChild(overlay);
  }
  resetDialogExit(overlay);
  overlay.innerHTML = [
    '<div class="modal-box" style="--modal-max-width:30rem;">',
    '<div class="modal-scroll" role="region" aria-label="Contenido del diálogo" tabindex="0">',
    '<div class="modal-header">',
    // .modal-header real usa justify-content:space-between (pensado para
    // "titulo + boton X"); con un solo hijo quedaria bien, pero icono+titulo
    // deben leerse como UN grupo pegado a la izquierda, no como 2 extremos
    // -- por eso van envueltos juntos en vez de ser 2 hijos directos.
    '<div style="display:flex; align-items:center; gap:0.75rem;">',
    '<span class="modal-icon-circle modal-icon-circle-danger"><span class="material-symbols-rounded" aria-hidden="true">lock_person</span></span>',
    '<h3 class="modal-title">Ya no tienes acceso a este módulo</h3>',
    '</div>',
    '</div>',
    '<div class="modal-body"><p>Tu perfil ya no incluye permiso para usar <strong>' + escapeHtml(moduleTitle) + '</strong>. Si crees que es un error, solicita acceso a un administrador.</p></div>',
    '</div>',
    '<div class="modal-footer modal-footer-center">',
    '<button class="btn btn-secondary" id="module-denied-ok-btn" type="button">',
    '<span class="material-symbols-rounded icon-sm" aria-hidden="true">grid_view</span>Ver módulos',
    '</button>',
    '</div></div>',
  ].join('');
  overlay.classList.remove('hidden');
  document.getElementById('module-denied-ok-btn').onclick = () => dismissDialog(overlay, () => window.location.reload());
  activateDialog(overlay);
}

/** "Ya no tienes acceso al sistema" -- doc 22 §8 (validacion en vivo),
 * variante MAS SEVERA de showModuleAccessDenied(): esta es para cuando
 * se deshabilita la CUENTA completa (usuario o proveedor) a mitad de
 * sesion, no un modulo o un permiso puntual. Por eso no hay "Ver
 * modulos" a donde mandarlo -- ya no puede ver NINGUNO -- el unico
 * camino es cerrar la sesion del lado del cliente y volver al login.
 * `message` viene tal cual del backend (`sessionService.hydrateSession`,
 * AUTH_USER_DISABLED / AUTH_PROVIDER_DISABLED): ya distingue "tu
 * usuario" vs "tu proveedor" sin duplicar ese texto aqui. Igual que
 * showModuleAccessDenied(): reload() y no `href`, por el mismo motivo
 * (dos portales, dos origenes distintos -- ver ese comentario). */
export function showAccountDisabled({ message } = {}) {
  let overlay = document.getElementById('account-disabled-modal');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'account-disabled-modal';
    overlay.className = 'modal-overlay hidden';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    document.body.appendChild(overlay);
  }
  resetDialogExit(overlay);
  overlay.innerHTML = [
    '<div class="modal-box" style="--modal-max-width:30rem;">',
    '<div class="modal-scroll" role="region" aria-label="Contenido del diálogo" tabindex="0">',
    '<div class="modal-header">',
    '<div style="display:flex; align-items:center; gap:0.75rem;">',
    '<span class="modal-icon-circle modal-icon-circle-danger"><span class="material-symbols-rounded" aria-hidden="true">person_off</span></span>',
    '<h3 class="modal-title">Ya no tienes acceso al sistema</h3>',
    '</div>',
    '</div>',
    '<div class="modal-body"><p>' + escapeHtml(message || 'Tu acceso fue revocado. Contacta a un administrador.') + '</p></div>',
    '</div>',
    '<div class="modal-footer modal-footer-center">',
    '<button class="btn btn-danger-primary" id="account-disabled-ok-btn" type="button">Salir</button>',
    '</div></div>',
  ].join('');
  overlay.classList.remove('hidden');
  document.getElementById('account-disabled-ok-btn').onclick = () => {
    // clearSession() + reload() EN EL MISMO ORIGEN (no href='/'): limpia
    // el remember-token interno (si aplica) para no desperdiciar un
    // resume() que sabemos que va a fallar, y recarga la pagina actual --
    // tanto App.jsx como ProveedorApp.jsx ya saben mostrar su propio
    // login cuando el resume no trae sesion valida.
    clearSession();
    dismissDialog(overlay, () => window.location.reload());
  };
  activateDialog(overlay);
}

/** Reporte de Drivers manda TIMESTAMPTZ/DATE crudos (`::text` en el
 * repositorio) -- el resto de la app recibe sus fechas YA formateadas del
 * backend, así que este par vive aquí, sin duplicar en cada archivo. */
export function formatDateOnly(value) {
  const clean = String(value ?? '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(clean);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : clean;
}

/** TIMESTAMPTZ::text ("2026-08-22 07:15:23.123+00") -> "22/08/2026 01:15".
 * La zona llega del contexto de tienda cuando existe; Centro es el
 * fallback para no depender jamás del huso configurado en el navegador. */
export function formatDateTime(value, timeZone = 'America/Mexico_City') {
  if (!value) return '';
  const raw = String(value).trim().replace(' ', 'T').replace(/([+-]\d\d)$/, '$1:00');
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const part = (type) => parts.find((item) => item.type === type)?.value || '';
  return `${part('day')}/${part('month')}/${part('year')} ${part('hour')}:${part('minute')}`;
}
